import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Loader2, X, Star } from "lucide-react";
import { getTemoignages, createTemoignage, updateTemoignage, deleteTemoignage } from "@/lib/api";

export const TemoignagesCMS = () => {
  const [temoignages, setTemoignages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    nom: "",
    texte: "",
    photo: "",
    type_temoignage: "proprietaire",
    note: 5
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await getTemoignages();
      setTemoignages(res.data);
    } catch (error) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateTemoignage(editing.id, formData);
        toast.success("Témoignage mis à jour !");
      } else {
        await createTemoignage(formData);
        toast.success("Témoignage créé !");
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleEdit = (tem) => {
    setEditing(tem);
    setFormData({
      nom: tem.nom,
      texte: tem.texte,
      photo: tem.photo || "",
      type_temoignage: tem.type_temoignage,
      note: tem.note
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    try {
      await deleteTemoignage(id);
      toast.success("Témoignage supprimé !");
      loadData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ nom: "", texte: "", photo: "", type_temoignage: "proprietaire", note: 5 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-[#004333]" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nouveau témoignage
        </button>
      </div>

      {temoignages.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {temoignages.map((tem) => (
            <div key={tem.id} className="bg-white border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#004333] rounded-full flex items-center justify-center text-white font-semibold">
                    {tem.nom.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#004333]">{tem.nom}</h4>
                    <span className="text-sm text-gray-500 capitalize">{tem.type_temoignage}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  {[...Array(tem.note)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#C9922A] text-[#C9922A]" />
                  ))}
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4 line-clamp-3">"{tem.texte}"</p>
              <div className="flex justify-end gap-2">
                <button onClick={() => handleEdit(tem)} className="p-2 text-gray-400 hover:text-[#004333]">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(tem.id)} className="p-2 text-gray-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 p-12 text-center text-gray-500">
          Aucun témoignage. Ajoutez des avis de vos clients.
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-display text-xl text-[#004333]">
                {editing ? "Modifier le témoignage" : "Nouveau témoignage"}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <X className="w-6 h-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                  <input
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="input-hh"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <select
                    value={formData.type_temoignage}
                    onChange={(e) => setFormData({ ...formData, type_temoignage: e.target.value })}
                    className="input-hh"
                  >
                    <option value="proprietaire">Propriétaire</option>
                    <option value="voyageur">Voyageur</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Témoignage *</label>
                <textarea
                  rows={4}
                  required
                  value={formData.texte}
                  onChange={(e) => setFormData({ ...formData, texte: e.target.value })}
                  className="input-hh"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setFormData({ ...formData, note: n })}
                      className="p-1"
                    >
                      <Star className={`w-6 h-6 ${n <= formData.note ? "fill-[#C9922A] text-[#C9922A]" : "text-gray-300"}`} />
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Annuler
                </button>
                <button type="submit" className="btn-primary flex-1">
                  {editing ? "Mettre à jour" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemoignagesCMS;
